import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTheme } from '@vitvio/design-system';
import { dayjs, toZonedTime } from '@vitvio/helpers';
import { useUserContext } from '@vitvio/user-context';

/**
 * Hook to calculate timeline position based on current time.
 * Uses own timer with update rate calculated from height and display density.
 * Returns raw decimal values - GPU handles subpixel positioning via CSS transform.
 */
export function useTimelinePosition(): { position: number; updateInterval: number } {
  const { getLayout } = useTheme();
  const { hospitalTimezone } = useUserContext();

  const minuteHeight = getLayout('timeline', 'minuteHeight', false);

  const dpr = window?.devicePixelRatio ?? 1;

  // Calculate update interval: time needed to move 1 physical pixel
  // pixels per ms = minuteHeight / (60 * 1000)
  // ms per physical pixel = (60 * 1000) / (minuteHeight * dpr)
  const updateInterval = useMemo(
    () => Math.max(100, Math.floor((60 * 1000) / (minuteHeight * dpr))),
    [minuteHeight, dpr],
  );

  const calculatePosition = useCallback(() => {
    const now = toZonedTime(dayjs(), hospitalTimezone);
    if (!now) return 0;

    const hours = now.hour();
    const minutes = now.minute();
    const seconds = now.second();
    const milliseconds = now.millisecond();

    const elapsedMinutes = hours * 60 + minutes + (seconds + milliseconds / 1000) / 60;
    return minuteHeight * elapsedMinutes;
  }, [hospitalTimezone, minuteHeight]);

  const [position, setPosition] = useState(calculatePosition);

  useEffect(() => {
    const timer = setInterval(() => {
      setPosition(calculatePosition());
    }, updateInterval);

    return () => clearInterval(timer);
  }, [calculatePosition, updateInterval]);

  return { position, updateInterval };
}
